<script setup lang="ts">
import { type Component, computed, markRaw, ref, watch } from 'vue';
import { useDisplay } from 'vuetify';
import {
  PolicyEnforcement,
  ScreeningType,
  V1SnapshotCommunicationTone,
  V1SnapshotMessagingWindow,
  V1SnapshotPushiness,
  V1SnapshotScoreThreshold,
  V1SnapshotStrictness,
  V1SnapshotThoroughness,
} from '@factoryfixinc/ats-interfaces';

import CopilotConfigurationTemplateService from '@/core/copilot-configuration-template/copilot-configuration-template.service';
import { ErrorService } from '@/core/shared/errors/error.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import { EmployerProfileService } from '@/core/shared/employer-profile/employer-profile.service';

import PersonalitySection from '@/components/CopilotPersonalities/CopilotPersonalityCreationModal/sections/PersonalitySection.vue';
import CompanyContextSection from '@/components/CopilotPersonalities/CopilotPersonalityCreationModal/sections/CompanyContextSection.vue';
import SourcingStrategySection from '@/components/CopilotPersonalities/CopilotPersonalityCreationModal/sections/SourcingStrategySection.vue';
import ScreeningStrategySection from '@/components/CopilotPersonalities/CopilotPersonalityCreationModal/sections/ScreeningStrategySection.vue';
import BaseModal from '@/components/Shared/Modals/BaseModal.vue';
import ProjectCopilotConfigurationUtils from '@/utils/project/project-copilot-configuration.utils';
import type { CopilotConfigurationFormData } from '@/components/CopilotPersonalities/types/copilot-configuration-template.interface';

const COPILOT_PERSONALITY_MODAL_CONTENT_ID = 'copilot-personality-modal-content';

interface SectionListData {
  [key: string]: {
    key: string;
    nextSection?: string;
    nextSectionLabel?: string;
    previousSection?: string;
    label: string;
    component: Component;
  };
}

// Props
// ===============================
const props = defineProps<{
  modelValue: boolean; // Controls dialog visibility
  copilotConfigurationTemplateId?: number; // If provided, modal is in edit mode
  candidateAiDisclosurePolicy?: PolicyEnforcement;
  isSaving?: boolean; // Parent's save request is in flight
}>();

// Emits
// ===============================
const emit = defineEmits<{
  (e: 'update:modelValue', payload: boolean): void;
  (e: 'close'): void;
  (
    e: 'save',
    payload: {
      data: CopilotConfigurationFormData;
      templateId?: number;
    },
  ): void;
}>();

// Services
// ===============================
const copilotConfigurationTemplateService = new CopilotConfigurationTemplateService();
const employerProfileService = new EmployerProfileService();

// Reactive State (Refs)
// ===============================
const copilotConfigurationData = ref<CopilotConfigurationFormData>({
  strictness: V1SnapshotStrictness.BALANCED,
  relevantSkills: '',
  relevantBackground: '',
  immediateRejectionReason: '',
  scoreThreshold: V1SnapshotScoreThreshold.THREE_PLUS,
  targetCompanies: '',
  avoidCompanies: '',
  copilotName: '',
  communicationTone: V1SnapshotCommunicationTone.FRIENDLY_PERSONAL,
  pushiness: V1SnapshotPushiness.COMMON_SENSE,
  thoroughness: V1SnapshotThoroughness.STANDARD,
  messagingWindow: V1SnapshotMessagingWindow.WHENEVER_ENGAGED,
  sellingPoints: '',
  perksAndPrograms: '',
  employerType: '',
  productsAndServices: '',
  competitorsAndDifferentiation: '',
  employerDescription: '',
  employeeBenefits: '',
  employerTraditionsAndMission: '',
  activeOutreachEnabled: true,
  scheduledUpdates: true,
  screeningType: ScreeningType.DYNAMIC,
  includeAiDisclosure: false,
  copilotDescription: '',
});

const selectedSection = ref<string>('personality');
const hasUnsavedChanges = ref(false);
const showExitConfirmation = ref(false);
const initialConfigurationData = ref<string>('');
const sectionListData = ref<SectionListData>({
  personality: {
    key: 'personality',
    nextSection: 'companyContext',
    nextSectionLabel: 'Company context',
    label: 'Personality',
    component: markRaw(PersonalitySection),
  },
  companyContext: {
    key: 'companyContext',
    nextSection: 'sourcingStrategy',
    nextSectionLabel: 'Sourcing strategy',
    previousSection: 'personality',
    label: 'Company context',
    component: markRaw(CompanyContextSection),
  },
  sourcingStrategy: {
    key: 'sourcingStrategy',
    nextSection: 'screeningStrategy',
    nextSectionLabel: 'Screening strategy',
    previousSection: 'companyContext',
    label: 'Sourcing strategy',
    component: markRaw(SourcingStrategySection),
  },
  screeningStrategy: {
    key: 'screeningStrategy',
    previousSection: 'sourcingStrategy',
    label: 'Screening strategy',
    component: markRaw(ScreeningStrategySection),
  },
});

// Computed Properties
// ===============================
const isOpen = computed<boolean>({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const currentSectionData = computed(() => {
  const sections = sectionListData.value;
  const currentSection = sections[selectedSection.value];

  return currentSection;
});

// Mobile: the desktop two-pane layout (fixed left nav + content) doesn't fit a
// phone, so below md the modal goes fullscreen with a top stepper + Back/Next.
const display = useDisplay();
const isMobile = computed(() => display.smAndDown.value);
const orderedSections = computed(() => Object.values(sectionListData.value));
const currentStepIndex = computed(() =>
  orderedSections.value.findIndex((section) => section.key === selectedSection.value),
);
const totalSteps = computed(() => orderedSections.value.length);

const isFirstSection = computed<boolean>(() => currentSectionData.value.key === 'personality');
const isLastSection = computed<boolean>(() => currentSectionData.value.key === 'screeningStrategy');
const isEditMode = computed<boolean>(() => Boolean(props.copilotConfigurationTemplateId));

const isShowingPreviousBtn = computed<boolean>(() => !isFirstSection.value);
const isShowingNextBtn = computed<boolean>(() => !isLastSection.value);
const isShowingSaveBtn = computed<boolean>(() => isLastSection.value);

const nextBtnLabel = computed<string>(() => {
  let label = 'Next';

  if (currentSectionData.value.nextSectionLabel) {
    label += `: ${currentSectionData.value.nextSectionLabel}`;
  }

  return label;
});
const saveBtnLabel = computed<string>(() =>
  isEditMode.value ? 'Save changes' : 'Create AI recruiter',
);
const modalTitle = computed<string>(() =>
  isEditMode.value ? 'Edit AI Recruiter' : 'Create your new AI recruiter',
);

async function prefillEmployerProfileData() {
  try {
    // Fetch employer profile if not already loaded
    if (!employerProfileService.employerProfile) {
      await employerProfileService.fetchAndSetEmployerProfile();
    }

    const profile = employerProfileService.employerProfile;
    if (profile) {
      // Only update fields that are empty (preserve any existing data)
      copilotConfigurationData.value = {
        ...copilotConfigurationData.value,
        employerType: copilotConfigurationData.value.employerType || profile.employerType || '',
        productsAndServices:
          copilotConfigurationData.value.productsAndServices || profile.productsAndServices || '',
        competitorsAndDifferentiation:
          copilotConfigurationData.value.competitorsAndDifferentiation ||
          profile.competitorsAndDifferentiation ||
          '',
        employerDescription:
          copilotConfigurationData.value.employerDescription || profile.employerDescription || '',
        employeeBenefits:
          copilotConfigurationData.value.employeeBenefits || profile.employeeBenefits || '',
        employerTraditionsAndMission:
          copilotConfigurationData.value.employerTraditionsAndMission ||
          profile.employerTraditionsAndMission ||
          '',
      };
    }
  } catch (error) {
    // Silently fail - prefilling is not critical
    ErrorService.captureException(error);
  }
}

// Methods
// ===============================
function resetForm() {
  selectedSection.value = 'personality';

  copilotConfigurationData.value = {
    strictness: V1SnapshotStrictness.BALANCED,
    relevantSkills: '',
    relevantBackground: '',
    immediateRejectionReason: '',
    scoreThreshold: V1SnapshotScoreThreshold.THREE_PLUS,
    targetCompanies: '',
    avoidCompanies: '',
    copilotName: '',
    communicationTone: V1SnapshotCommunicationTone.FRIENDLY_PERSONAL,
    pushiness: V1SnapshotPushiness.COMMON_SENSE,
    thoroughness: V1SnapshotThoroughness.STANDARD,
    messagingWindow: V1SnapshotMessagingWindow.WHENEVER_ENGAGED,
    sellingPoints: '',
    perksAndPrograms: '',
    employerType: '',
    productsAndServices: '',
    competitorsAndDifferentiation: '',
    employerDescription: '',
    employeeBenefits: '',
    employerTraditionsAndMission: '',
    activeOutreachEnabled: true,
    scheduledUpdates: true,
    screeningType: ScreeningType.DYNAMIC,
    includeAiDisclosure: false,
    copilotDescription: '',
  };

  hasUnsavedChanges.value = false;
  initialConfigurationData.value = JSON.stringify(copilotConfigurationData.value);
}

function closeModal() {
  if (props.isSaving) return;

  if (hasUnsavedChanges.value) {
    showExitConfirmation.value = true;
  } else {
    confirmClose();
  }
}

function confirmClose() {
  if (props.isSaving) return;

  emit('update:modelValue', false);
  emit('close');
  showExitConfirmation.value = false;
  hasUnsavedChanges.value = false;
}

function cancelClose() {
  showExitConfirmation.value = false;
}

function scrollMainContentToTop() {
  const mainContent = document.getElementById(COPILOT_PERSONALITY_MODAL_CONTENT_ID);
  if (mainContent) {
    mainContent.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

function navigateToSection(sectionKey?: string) {
  if (sectionKey) {
    selectedSection.value = sectionKey;
    scrollMainContentToTop();
  }
}

function saveCopilotConfiguration() {
  if (props.isSaving) return;

  emit('save', {
    data: copilotConfigurationData.value,
    templateId: props.copilotConfigurationTemplateId,
  });
}

async function setCopilotConfigurationTemplate(id: number) {
  try {
    const template = await copilotConfigurationTemplateService.getTemplateById(id);

    if (template) {
      const snapshot = ProjectCopilotConfigurationUtils.parseCopilotConfigurationSnapshot(
        template.snapshot ?? '',
        template.version,
      );
      copilotConfigurationData.value = {
        ...snapshot,
        copilotDescription: template.description,
        copilotName: template.displayName,
      };
    }
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Error fetching recruiter, try reloading the page');
  }
}

// Lifecycle Hooks
// ===============================
watch(
  () => props.modelValue,
  async (value) => {
    if (value) {
      resetForm();
      scrollMainContentToTop();

      if (props.copilotConfigurationTemplateId) {
        await setCopilotConfigurationTemplate(props.copilotConfigurationTemplateId);
      } else {
        // Only prefill for new copilots, not when editing existing ones
        await prefillEmployerProfileData();
      }
    }
  },
);

watch(
  copilotConfigurationData,
  (newValue) => {
    const currentData = JSON.stringify(newValue);

    if (initialConfigurationData.value && currentData !== initialConfigurationData.value) {
      hasUnsavedChanges.value = true;
    }
  },
  { deep: true },
);
</script>

<template>
  <v-dialog
    :model-value="isOpen"
    :max-width="isMobile ? undefined : '900px'"
    :fullscreen="isMobile"
    persistent
    @update:modelValue="closeModal"
  >
    <div :class="isMobile ? 'flex h-full flex-col bg-white' : ''">
      <!-- Header -->
      <div
        class="flex items-center justify-between border-b border-b-tint-60 bg-white max-md:shrink-0 max-md:p-4 max-md:pt-[max(1rem,env(safe-area-inset-top))] md:h-[93px] md:rounded-t-2xl md:p-8"
      >
        <span class="font-serif font-black text-highlight-1000 max-md:text-xl md:text-2xl">
          {{ modalTitle }}
        </span>
        <img
          class="cursor-pointer"
          data-test="copilot-personality-close"
          src="@/assets/svg/close-black.svg"
          alt="close"
          width="20"
          height="20"
          @click="closeModal"
        />
      </div>

      <!-- Mobile stepper (replaces the desktop left sidebar) -->
      <div v-if="isMobile" class="shrink-0 border-b border-b-tint-60 bg-white px-4 py-3">
        <div class="mb-2 flex items-center justify-between">
          <span class="text-sm font-bold text-highlight-600">{{ currentSectionData.label }}</span>
          <span class="text-xs font-medium text-shade-800">
            Step {{ currentStepIndex + 1 }} of {{ totalSteps }}
          </span>
        </div>
        <div class="flex gap-1.5">
          <div
            v-for="(section, index) in orderedSections"
            :key="section.key"
            class="h-1 flex-1 rounded-full"
            :class="index <= currentStepIndex ? 'bg-highlight-600' : 'bg-tint-80'"
          />
        </div>
      </div>

      <!-- Content -->
      <div :class="isMobile ? 'flex min-h-0 flex-1' : 'flex h-[calc(100dvh-261px)]'">
        <!-- Left Sidebar (desktop only; mobile uses the top stepper above) -->
        <div class="h-full min-w-[250px] bg-highlight-0 px-4 py-5 max-md:hidden">
          <div
            v-for="section in sectionListData"
            :key="section.key"
            class="mb-[7px] h-12 cursor-pointer rounded-[4px] py-3 pl-3 text-base hover:bg-highlight-50"
            :class="{
              'border-l-4 border-l-highlight-600 bg-highlight-50 font-bold text-highlight-600':
                selectedSection === section.key,
              'font-normal text-shade-800': selectedSection !== section.key,
            }"
            @click="navigateToSection(section.key)"
          >
            {{ section.label }}
          </div>
        </div>

        <!-- Main Content -->
        <div
          :id="COPILOT_PERSONALITY_MODAL_CONTENT_ID"
          class="h-full flex-grow overflow-y-scroll bg-white px-4 py-8"
        >
          <component
            :is="currentSectionData.component"
            v-model="copilotConfigurationData"
            v-bind="
              currentSectionData.key === 'personality'
                ? { candidateAiDisclosurePolicy: props.candidateAiDisclosurePolicy }
                : {}
            "
          />
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex items-center justify-end border-t border-t-tint-60 bg-white max-md:shrink-0 max-md:justify-between max-md:gap-3 max-md:p-4 max-md:pb-[max(1rem,env(safe-area-inset-bottom))] md:h-[72px] md:rounded-b-2xl md:p-8"
      >
        <!-- Previous Button -->
        <button
          v-show="isShowingPreviousBtn"
          type="button"
          class="rounded-md border-2 border-highlight-1000 px-4 py-2.5 text-sm font-semibold max-md:min-h-[44px]"
          @click="navigateToSection(currentSectionData.previousSection)"
        >
          Back
        </button>

        <!-- Next Button -->
        <button
          v-show="isShowingNextBtn"
          type="button"
          class="rounded-md border-2 border-shade-900 bg-shade-900 px-4 py-2.5 text-sm font-semibold text-white max-md:min-h-[44px] max-md:flex-1 md:ml-4"
          @click="navigateToSection(currentSectionData.nextSection)"
        >
          <span class="md:hidden">Next</span>
          <span class="max-md:hidden">{{ nextBtnLabel }}</span>
        </button>

        <!-- Save Button -->
        <v-btn
          v-show="isShowingSaveBtn"
          data-test="copilot-personality-save"
          :ripple="false"
          :loading="isSaving"
          variant="flat"
          class="!box-border !h-11 !rounded-md !bg-highlight-600 !px-[18px] !text-sm !font-semibold !normal-case !tracking-normal !text-white max-md:flex-1 md:ml-4"
          @click="saveCopilotConfiguration"
          >{{ saveBtnLabel }}</v-btn
        >
      </div>
    </div>
  </v-dialog>

  <!-- Exit Confirmation Modal -->
  <BaseModal
    v-model="showExitConfirmation"
    title="Exit without saving?"
    subtitle="You are about to exit without saving your changes."
    :show-close-button="true"
    :show-cancel-button="true"
    :show-secondary-button="false"
    cancel-button-text="Cancel"
    submit-button-text="Discard changes"
    max-width="500px"
    @cancel="cancelClose"
    @submit="confirmClose"
  />
</template>
